import type { OrderStatusValue } from '../../lib/orders-api'

// D5 STATE-AWARE aging (bench-HOP-ORDER-BOARD.CB-A) — NOT one clock. Three buckets:
//  (a) PENDING/unaccepted        → age on time-SINCE-placed (elapsed; bigger = worse)
//  (b) active in-prep            → age on time-UNTIL-promised (remaining; smaller = worse)
//  (c) far-out scheduled/pre-order → NEUTRAL until the prep window opens
//
// Thresholds are the same configurable 5 / 8 minute cutoffs for both readings, but the
// color direction is INVERTED between them: for elapsed time, small is safe (green) and
// large is stale (red); for remaining time, small is urgent (red) and large is safe
// (green) — applying the literal green-0-5/red->8 mapping unmodified to a "minutes
// remaining" value would nonsensically mark an order 2 minutes from its promised time as
// "green" (safe), so bandForRemaining() mirrors the same cutoffs with the color order
// flipped to match urgency direction.
export const AGING_THRESHOLD_MIN = { green: 5, yellow: 8 } as const

export type AgingBand = 'green' | 'yellow' | 'red' | 'neutral'
// 'handoff' — bench-BOARD-AGES-IN-HOURS §4-RULED ①: READY_FOR_DISPATCH keeps its dwell
// clock (minutes stays populated, same source value as 'elapsed'/'remaining') but the
// urgency COLOUR is not the render's to show — dispatch's write, not prep's, closes this
// row. Always paired with band: 'neutral'; the render layer reuses the status badge label
// for it rather than "waiting"/"due" (no new copy — see CLAUDE.md §4 copy constraint).
export type AgingKind = 'elapsed' | 'remaining' | 'neutral' | 'handoff'

export interface AgingResult {
  kind: AgingKind
  band: AgingBand
  minutes: number | null
}

// Statuses HOP's board actively preps toward — the v1 net scope ("advance-through-PREP-
// only"). READY_FOR_DISPATCH is included (still "in prep" from HOP's read perspective) even
// though it's outside PREP_ADVANCE_SOURCES (orders-api.ts) — the positive advance-button
// allowlist and this aging-relevance set are deliberately different: RFD gets no advance
// button (its only FORWARD_TRANSITIONS target, OUT_FOR_DELIVERY, is dispatch's write), but
// it still shows aging urgency since HOP is still tracking it. OUT_FOR_DELIVERY/DELIVERED/
// CANCELLED/REFUNDED are outside prep-aging scope entirely → neutral (no urgency signal).
const IN_PREP_STATUSES = new Set<OrderStatusValue>([
  'CONFIRMED', 'IN_KITCHEN', 'AWAITING_RETAIL_PACK', 'AWAITING_FINAL_CHECK', 'READY_FOR_DISPATCH',
])

function minutesBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / 60000)
}

function bandForElapsed(minutes: number): AgingBand {
  if (minutes <= AGING_THRESHOLD_MIN.green) return 'green'
  if (minutes <= AGING_THRESHOLD_MIN.yellow) return 'yellow'
  return 'red'
}

function bandForRemaining(minutes: number): AgingBand {
  if (minutes <= AGING_THRESHOLD_MIN.green) return 'red'
  if (minutes <= AGING_THRESHOLD_MIN.yellow) return 'yellow'
  return 'green'
}

// UTC calendar-day compare — deliveryDate is stored/returned as a bare @db.Date (UTC
// midnight), so comparing UTC y/m/d avoids local-timezone drift flipping the bucket.
function isFutureUtcDate(deliveryDate: Date, now: Date): boolean {
  const dayMs = 24 * 60 * 60 * 1000
  const dDay = Math.floor(deliveryDate.getTime() / dayMs)
  const nowDay = Math.floor(now.getTime() / dayMs)
  return dDay > nowDay
}

function endOfUtcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999))
}

export interface AgingOrder {
  status: OrderStatusValue
  placedAt: string
  deliveryDate: string | null
}

export function computeAging(order: AgingOrder, now: Date): AgingResult {
  if (order.status === 'PENDING') {
    const minutes = minutesBetween(new Date(order.placedAt), now)
    return { kind: 'elapsed', minutes, band: bandForElapsed(minutes) }
  }

  if (!IN_PREP_STATUSES.has(order.status)) {
    return { kind: 'neutral', minutes: null, band: 'neutral' }
  }

  // READY_FOR_DISPATCH still gets the same dwell clock below (deliveryDate remaining, or
  // elapsed-since-placed) — only the reported kind/band are swapped for 'handoff'/'neutral'
  // just before returning, per the ruling above.
  const isHandoff = order.status === 'READY_FOR_DISPATCH'

  if (order.deliveryDate) {
    const deliveryDate = new Date(order.deliveryDate)
    // (c) far-out scheduled/pre-order — deliveryDate is a future calendar day, the prep
    // window hasn't opened yet.
    if (isFutureUtcDate(deliveryDate, now)) {
      return { kind: 'neutral', minutes: null, band: 'neutral' }
    }
    // deliverySlot has no fixed vocabulary anywhere in this codebase (free-text field,
    // rendered as-is in DeliverySection) — there's no slot→clock-time table to compute a
    // precise promised timestamp from. Using end-of-day on the promised calendar date as
    // the deadline is the most faithful reading of "time-until-PROMISED" available from
    // deliveryDate alone; slot itself is still shown to the operator (OrderCard), just not
    // fed into this countdown. Flagged for Rook/Frank if a slot→time mapping exists elsewhere.
    const minutes = minutesBetween(now, endOfUtcDay(deliveryDate))
    if (isHandoff) return { kind: 'handoff', minutes, band: 'neutral' }
    return { kind: 'remaining', minutes, band: bandForRemaining(minutes) }
  }

  // No scheduled deliveryDate — an ASAP/immediate order with no promised clock to count
  // down to. Falls back to elapsed-since-placed (same signal as PENDING) as the best
  // available urgency proxy: how long has this been sitting since it entered the queue.
  const minutes = minutesBetween(new Date(order.placedAt), now)
  if (isHandoff) return { kind: 'handoff', minutes, band: 'neutral' }
  return { kind: 'elapsed', minutes, band: bandForElapsed(minutes) }
}

// bench-BOARD-AGES-IN-HOURS §4-RULED ②: one unit ladder for every rendered aging value —
// minutes under an hour, then "Nh Nm" under 48h, then whole days past that. Takes the
// already-unsigned magnitude; callers (waiting/due/overdue/handoff) apply Math.abs first,
// same as before this bench (the sign was never the bug, see bench §1②).
export function formatAgingDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  if (minutes <= 48 * 60) {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}h ${mins}m`
  }
  const days = Math.floor(minutes / (24 * 60))
  return `${days}d`
}
